import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "../Icon";
import { toHex6 } from "../fabricKit";

type PopoverAlign = "left" | "right";
type ColorPickerVariant = "dot" | "letter" | "ring";
export type GlyphKind =
  | "rect"
  | "rounded"
  | "pill"
  | "circle"
  | "triangle"
  | "star"
  | "line-solid"
  | "line-dashed"
  | "line-dotted"
  | "line-arrow"
  | "line-double"
  | "frame-rect"
  | "frame-rounded"
  | "frame-circle";

type GridCell = [number, number, number, number];

interface PopoverProps {
  label: ReactNode;
  title?: string;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: PopoverAlign;
  width?: number | string;
  className?: string;
  buttonClass?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface ColorPickerProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  label: string;
  variant?: ColorPickerVariant;
  brandColors?: string[];
  docColors?: () => string[];
  allowNone?: boolean;
}

interface SwatchProps {
  c: string;
  on: boolean;
  onPick: (color: string) => void;
}

interface NameFieldProps {
  value: string;
  onSave: (value: string) => void;
}

interface NumberStepperProps {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label: string;
}

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  onCommit?: () => void;
  suffix?: string;
}

interface NumberBoxProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  suffix?: string;
}

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number | string;
  footer?: ReactNode;
}

interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  items: Array<[string, string]>;
}

interface GlyphProps {
  d?: string;
  kind?: GlyphKind;
  size?: number;
}

interface FrameGlyphProps {
  d: string;
}

interface GridGlyphProps {
  cells: GridCell[];
}

interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropperInstance {
  open: () => Promise<EyeDropperResult>;
}

interface EyeDropperConstructor {
  new (): EyeDropperInstance;
}

type EyeDropperWindow = Window & {
  EyeDropper?: EyeDropperConstructor;
};

export const PALETTE = [
  "#000000",
  "#545454",
  "#737373",
  "#a6a6a6",
  "#d9d9d9",
  "#ffffff",
  "#ff3131",
  "#ff5757",
  "#ff66c4",
  "#cb6ce6",
  "#8c52ff",
  "#5e17eb",
  "#03989e",
  "#0b7a5a",
  "#25d366",
  "#7ed957",
  "#c1ff72",
  "#ffde59",
  "#0097b2",
  "#0cc0df",
  "#5ce1e6",
  "#38b6ff",
  "#5271ff",
  "#004aad",
  "#ffbd59",
  "#ff914d",
  "#f97316",
  "#e11d48",
  "#7c2d12",
  "#10231e",
];

/* Shared button styles reused by the popover trigger (st-btn st-btn-small). */
const DEFAULT_POPOVER_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 min-h-[34px] px-[11px] py-[7px] " +
  "rounded-[9px] border border-st-line bg-white text-st-ink font-semibold text-[13px] " +
  "leading-none whitespace-nowrap cursor-pointer hover:border-[#9fb3ab] " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none";

/** A small popover anchored to a toolbar button. */
export function Popover({
  label,
  title,
  children,
  align = "left",
  width,
  className = "",
  buttonClass = DEFAULT_POPOVER_BUTTON_CLASS,
  open: controlled,
  onOpenChange,
}: PopoverProps) {
  const [own, setOwn] = useState(false);
  const open = controlled ?? own;
  const setOpen = (v: boolean) => {
    setOwn(v);
    onOpenChange?.(v);
  };
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e: MouseEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      )
        setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", off);
      document.removeEventListener("keydown", esc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return (
    <div className={`relative z-[100] ${className}`} ref={ref}>
      <button
        type="button"
        className={buttonClass}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        title={title}
      >
        {label}
      </button>
      {open && (
        <div
          className={`absolute top-[calc(100%+8px)] z-[9999] w-[260px] px-3.5 py-3 rounded-2xl
            bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35),0_0_0_1px_rgba(16,35,30,0.06)]
            pointer-events-auto ${align === "right" ? "left-auto right-0" : "left-0"}`}
          style={width ? { width } : undefined}
          role="dialog"
          aria-label={title || (typeof label === "string" ? label : "Options")}
        >
          {typeof children === "function"
            ? children(() => setOpen(false))
            : children}
        </div>
      )}
    </div>
  );
}

/**
 * Colour picker like Canva's: document colours, brand colours, a default palette, any colour, hex and eyedropper.
 * variant: dot | letter (text colour) | ring (border)
 */
export function ColorPicker({
  value,
  onChange,
  label,
  variant = "dot",
  brandColors = [],
  docColors = () => [],
  allowNone = false,
}: ColorPickerProps) {
  const hex = value && value !== "transparent" ? toHex6(value) : null;
  const [draft, setDraft] = useState(hex || "");
  const [docs, setDocs] = useState<string[]>([]);
  useEffect(() => setDraft(hex || ""), [hex]);

  const pick = async (): Promise<void> => {
    try {
      // eslint-disable-next-line no-undef
      const EyeDropper = (window as EyeDropperWindow).EyeDropper;
      if (!EyeDropper) return;
      const r = await new EyeDropper().open();
      onChange(r.sRGBHex);
    } catch {
      /* cancelled */
    }
  };

  const face =
    variant === "letter" ? (
      <span
        className="inline-block font-bold text-[17px] leading-none border-b-[3px] border-solid px-0.5 pb-px font-['Poppins',sans-serif]"
        style={{ borderBottomColor: hex || "#000" }}
      >
        A
      </span>
    ) : (
      <span
        className="inline-block w-5 h-5 rounded-full border border-black/[0.18]"
        style={
          variant === "ring"
            ? {
                borderColor: hex || "#ccc",
                borderWidth: 3,
                borderStyle: "solid",
                background: "transparent",
              }
            : {
                background:
                  hex ||
                  "repeating-conic-gradient(#ddd 0 25%, #fff 0 50%) 0 0/10px 10px",
              }
        }
      />
    );

  return (
    <Popover
      label={face}
      title={label}
      buttonClass="relative w-9 h-9 grid place-items-center rounded-[9px] border border-st-line cursor-pointer bg-white"
      width={268}
      onOpenChange={(o) => o && setDocs(docColors())}
    >
      <div className="grid gap-1.5">
        {docs.length > 0 && (
          <>
            <p className="flex items-center gap-[5px] mt-1.5 mb-0.5 text-xs font-bold text-st-muted">
              Document colours
            </p>
            <div className="flex flex-wrap gap-1.5 mb-1">
              {docs.map((c) => (
                <Swatch key={`d${c}`} c={c} on={c === hex} onPick={onChange} />
              ))}
            </div>
          </>
        )}
        {brandColors.length > 0 && (
          <>
            <p className="flex items-center gap-[5px] mt-1.5 mb-0.5 text-xs font-bold text-st-muted">
              <Icon name="brand" size={14} /> Brand colours
            </p>
            <div className="flex flex-wrap gap-1.5 mb-1">
              {brandColors.map((c) => (
                <Swatch
                  key={`b${c}`}
                  c={c}
                  on={c.toLowerCase() === hex}
                  onPick={onChange}
                />
              ))}
            </div>
          </>
        )}
        <p className="flex items-center gap-[5px] mt-1.5 mb-0.5 text-xs font-bold text-st-muted">
          Default colours
        </p>
        <div className="flex flex-wrap gap-1.5 mb-1">
          {allowNone && (
            <button
              className="w-[26px] h-[26px] rounded-full border border-[rgba(16,35,30,0.18)] cursor-pointer p-0
                bg-[linear-gradient(135deg,#fff_45%,#e11d48_45%,#e11d48_55%,#fff_55%)]"
              onClick={() => onChange(null)}
              aria-label="No colour"
              title="No colour"
            />
          )}
          {PALETTE.map((c) => (
            <Swatch key={c} c={c} on={c === hex} onPick={onChange} />
          ))}
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <label
            className="relative w-[30px] h-[30px] rounded-full flex-none cursor-pointer
              bg-[conic-gradient(red,yellow,lime,cyan,blue,magenta,red)]"
            title="Any colour"
          >
            <input
              type="color"
              className="absolute inset-0 opacity-0 cursor-pointer"
              value={hex || "#000000"}
              onChange={(e) => onChange(e.target.value)}
              aria-label={`${label}: any colour`}
            />
          </label>
          <input
            className="flex-1 min-h-[32px] px-[11px] py-[9px] rounded-[9px] border border-st-line
              bg-white text-st-ink font-mono text-[13px] lowercase"
            value={draft}
            maxLength={7}
            aria-label="Hex code"
            spellCheck={false}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && /^#?[0-9a-f]{6}$/i.test(draft))
                onChange(draft.startsWith("#") ? draft : `#${draft}`);
            }}
            onBlur={() => {
              if (/^#?[0-9a-f]{6}$/i.test(draft))
                onChange(draft.startsWith("#") ? draft : `#${draft}`);
            }}
          />
          {(window as EyeDropperWindow).EyeDropper && (
            <button
              className="w-[30px] h-[30px] rounded-[8px] grid place-items-center border-0
                bg-transparent text-st-muted cursor-pointer hover:bg-st-paper hover:text-st-ink"
              onClick={pick}
              title="Pick a colour from the screen"
              aria-label="Eyedropper"
            >
              <Icon name="eyedropper" size={16} />
            </button>
          )}
        </div>
      </div>
    </Popover>
  );
}

function Swatch({ c, on, onPick }: SwatchProps) {
  return (
    <button
      type="button"
      className={`w-[26px] h-[26px] rounded-full border border-[rgba(16,35,30,0.18)] cursor-pointer p-0 hover:scale-110 ${
        on ? "shadow-[0_0_0_2px_#fff,0_0_0_4px_theme(colors.st.green)]" : ""
      }`}
      style={{ background: c }}
      onClick={() => onPick(c)}
      aria-label={c}
      title={c}
    />
  );
}

export function NameField({ value, onSave }: NameFieldProps) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input
      className="bg-transparent border-st-line border px-2 py-1 w-36 rounded-md outline-none"
      value={v}
      aria-label="Design name"
      maxLength={120}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (v !== value) onSave(v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setV(value);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

export function NumberStepper({
  value,
  min,
  max,
  onChange,
  label,
}: NumberStepperProps) {
  const [v, setV] = useState<number | string>(value);
  useEffect(() => setV(value), [value]);

  const commit = (n: number | string) => {
    const x = Math.min(max, Math.max(min, Number(n) || min));
    setV(x);
    onChange(x);
  };

  return (
    <div className="inline-flex items-center border border-st-line rounded-[9px] overflow-hidden h-9">
      <button
        className="w-7 h-full border-0 bg-st-paper cursor-pointer grid place-items-center text-st-muted"
        onClick={() => commit(value - (value >= 40 ? 4 : 1))}
        aria-label={`Decrease ${label}`}
      >
        <Icon name="minus" size={14} />
      </button>
      <input
        className="w-11 h-full border-0 text-center text-sm [font-variant-numeric:tabular-nums] [font:inherit]"
        value={v}
        aria-label={label}
        inputMode="numeric"
        onChange={(e) => setV(e.target.value.replace(/\D/g, ""))}
        onBlur={() => commit(v)}
        onKeyDown={(e) => e.key === "Enter" && commit(v)}
      />
      <button
        className="w-7 h-full border-0 bg-st-paper cursor-pointer grid place-items-center text-st-muted"
        onClick={() => commit(value + (value >= 40 ? 4 : 1))}
        aria-label={`Increase ${label}`}
      >
        <Icon name="plus" size={14} />
      </button>
    </div>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  onCommit,
  suffix = "",
}: SliderProps) {
  return (
    <div className="my-3">
      <div className="flex justify-between text-[13px] mb-1">
        <span>{label}</span>
        <span className="text-st-muted [font-variant-numeric:tabular-nums]">
          {value}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        className="w-full accent-st-green"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
        onPointerUp={() => onCommit?.()}
        onKeyUp={() => onCommit?.()}
      />
    </div>
  );
}

/** A labelled number box (position and size). */
export function NumberBox({ label, value, onChange, suffix }: NumberBoxProps) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => {
    const n = Number(v);
    if (Number.isFinite(n)) onChange(n);
    else setV(String(value));
  };
  return (
    <label className="flex items-center gap-1.5 h-[38px] px-2.5 border border-st-line rounded-[9px] text-[13px] focus-within:border-st-green">
      <span className="text-st-muted font-semibold w-3.5">{label}</span>
      <input
        className="flex-1 min-w-0 border-0 outline-none [font-variant-numeric:tabular-nums] [font:inherit]"
        value={v}
        inputMode="decimal"
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && commit()}
      />
      {suffix && <em className="not-italic text-st-muted">{suffix}</em>}
    </label>
  );
}

export function Modal({
  title,
  onClose,
  children,
  width = 560,
  footer,
}: ModalProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", esc);

    ref.current?.focus();

    return () => {
      document.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  return (
    <div
      className="
        fixed inset-0 z-[1000]
        flex items-center justify-center
        bg-[rgba(9,20,17,0.45)]
        p-4
      "
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="
          flex
          max-h-[calc(100vh-32px)]
          w-full
          flex-col
          overflow-hidden
          rounded-xl
          shadow-[0_25px_80px_-20px_rgba(0,0,0,0.45)]
          outline-none
          p-4
          bg-white
        "
        style={{
          maxWidth: typeof width === "number" ? `${width}px` : width,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header
          className="
            flex
            shrink-0
            items-center
            justify-between
          "
        >
          <h2
            className="
              font-['Bricolage_Grotesque','IBM_Plex_Sans',sans-serif]
              text-xl
              font-bold
              text-st-ink
            "
          >
            {title}
          </h2>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="
              inline-flex
              h-9
              w-9
              shrink-0
              cursor-pointer
              items-center
              justify-center
              rounded-lg
              border-0
              bg-transparent
              text-st-muted
              transition
              hover:bg-st-paper
              hover:text-st-ink
              focus-visible:outline
              focus-visible:outline-[3px]
              focus-visible:outline-offset-2
              focus-visible:outline-[rgba(11,122,90,0.45)]
            "
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        {/* Body */}
        <div
          className="
            min-h-0
            flex-1
            overflow-y-auto
            py-2
          "
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <footer
            className="
              flex
              shrink-0
              flex-wrap
              items-center
              gap-2
              border-t
              border-st-line
              bg-st-paper
              px-[18px]
              py-3
            "
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function Tabs({ value, onChange, items }: TabsProps) {
  return (
    <div className="flex gap-0.5 border-b border-st-line mb-3" role="tablist">
      {items.map(([k, label]) => (
        <button
          key={k}
          role="tab"
          aria-selected={value === k}
          className={`flex-1 border-0 bg-transparent px-1.5 py-2.5 text-[13.5px] font-semibold
            border-b-2 -mb-px cursor-pointer ${
              value === k
                ? "text-st-ink border-st-green"
                : "text-st-muted border-transparent"
            }`}
          onClick={() => onChange(k)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** Little pictures of shapes, frames and lines for the Elements panel. */
export function Glyph({ d, kind, size = 40 }: GlyphProps) {
  if (d) {
    return (
      <svg
        viewBox="-4 -4 108 108"
        width={size}
        height={size}
        aria-hidden="true"
      >
        <path d={d} fill="currentColor" />
      </svg>
    );
  }
  const common = { fill: "currentColor" };
  const frame = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.5,
    strokeDasharray: "5 4",
  };
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
      {kind === "rect" && (
        <rect x="8" y="8" width="32" height="32" {...common} />
      )}
      {kind === "rounded" && (
        <rect x="4" y="12" width="40" height="24" rx="6" {...common} />
      )}
      {kind === "pill" && (
        <rect x="2" y="16" width="44" height="16" rx="8" {...common} />
      )}
      {kind === "circle" && <circle cx="24" cy="24" r="17" {...common} />}
      {kind === "triangle" && <path d="M24 7l18 33H6z" {...common} />}
      {kind === "star" && (
        <path
          d="M24 5l5.6 12.3 13.4 1.4-10 9 2.9 13.2L24 34.2 12.1 40.9 15 27.7l-10-9 13.4-1.4z"
          {...common}
        />
      )}
      {kind === "line-solid" && (
        <path
          d="M5 24h38"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        />
      )}
      {kind === "line-dashed" && (
        <path
          d="M5 24h38"
          stroke="currentColor"
          strokeWidth="4"
          strokeDasharray="7 5"
        />
      )}
      {kind === "line-dotted" && (
        <path
          d="M5 24h38"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="0.1 8"
        />
      )}
      {kind === "line-arrow" && (
        <path
          d="M5 24h37M33 15l9 9-9 9"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {kind === "line-double" && (
        <path
          d="M6 24h36M33 15l9 9-9 9M15 15l-9 9 9 9"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {kind === "frame-rect" && (
        <rect x="8" y="8" width="32" height="32" rx="2" {...frame} />
      )}
      {kind === "frame-rounded" && (
        <rect x="8" y="8" width="32" height="32" rx="9" {...frame} />
      )}
      {kind === "frame-circle" && <circle cx="24" cy="24" r="16" {...frame} />}
    </svg>
  );
}

export function FrameGlyph({ d }: FrameGlyphProps) {
  return (
    <svg viewBox="-6 -6 112 112" width={40} height={40} aria-hidden="true">
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="5"
        strokeDasharray="10 8"
      />
    </svg>
  );
}

export function GridGlyph({ cells }: GridGlyphProps) {
  return (
    <svg viewBox="0 0 48 48" width={40} height={40} aria-hidden="true">
      {cells.map(([x, y, w, h], i) => (
        // eslint-disable-next-line react/no-array-index-key
        <rect
          key={i}
          x={4 + x / 40 + 1}
          y={4 + y / 40 + 1}
          width={(w / 40) * 2}
          height={(h / 40) * 2}
          rx="2"
          fill="currentColor"
          opacity={0.25 + (i % 3) * 0.2}
        />
      ))}
    </svg>
  );
}